import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';

/**
 * The "what does this column actually count?" affordance.
 *
 * Every number on the admin boards is derived - "people" is a guess made from
 * visitor keys, "turns" ignores monologues, "never opened" is only as good as
 * read-receipt coverage - and a header word cannot carry that. Rather than
 * writing the caveats into the page copy, where they would bury the numbers,
 * each one hangs off the field it belongs to.
 *
 * Click, not hover: these sit in table headers a reader sweeps across, and a
 * bubble that opens on the way past is noise. Click also gives touch and
 * keyboard the same behaviour as a mouse.
 *
 * The bubble is position: fixed and measured on open, because both boards clip
 * their tables with overflow: hidden to round the corners - an absolutely
 * positioned bubble inside a th is cut off by it.
 */
@Component({
  selector: 'app-info-hint',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button
      type="button"
      class="dot"
      [attr.aria-expanded]="open"
      [attr.aria-label]="'What ' + label + ' means'"
      (click)="toggle($event)"
    >
      i
    </button>
    <div
      #bubble
      class="bubble"
      role="tooltip"
      *ngIf="open"
      [style.top.px]="top"
      [style.left.px]="left"
    >
      {{ text }}
    </div>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        vertical-align: middle;
        margin-left: 5px;
      }

      .dot {
        width: 14px;
        height: 14px;
        padding: 0;
        border: 0;
        border-radius: 50%;
        background: rgba(31, 111, 102, 0.13);
        color: #1f6f66;
        /* The header around this is uppercase, letter-spaced and bold; none of
           that belongs on a lowercase i in a 14px circle. */
        font: 700 9px/1 Georgia, 'Times New Roman', serif;
        font-style: italic;
        letter-spacing: 0;
        text-transform: none;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.15s, color 0.15s;
      }

      .dot:hover,
      .dot[aria-expanded='true'] {
        background: #1f6f66;
        color: #fff;
      }

      .dot:focus-visible {
        outline: 2px solid #1f6f66;
        outline-offset: 2px;
      }

      .bubble {
        position: fixed;
        z-index: 1000;
        width: 260px;
        padding: 11px 13px;
        border-radius: 10px;
        background: #12332f;
        color: #f4efe6;
        /* Reset the header's display font: this is prose, and it is sitting
           inside a th that is uppercase and letter-spaced. */
        font: 400 12.5px/1.5 system-ui, -apple-system, 'Segoe UI', sans-serif;
        letter-spacing: 0;
        text-transform: none;
        white-space: normal;
        text-align: left;
        box-shadow: 0 8px 24px rgba(18, 51, 47, 0.22);
      }
    `,
  ],
})
export class InfoHintComponent {
  /** The explanation itself. One or two sentences; this is not a manual. */
  @Input() text = '';
  /** Names the field for screen readers, which cannot see what it sits beside. */
  @Input() label = 'this field';

  open = false;
  top = 0;
  left = 0;

  @ViewChild('bubble') bubbleRef?: ElementRef<HTMLElement>;

  constructor(private host: ElementRef<HTMLElement>) {}

  toggle(event: MouseEvent): void {
    /* The hint often sits in a header or a stat tile that is itself clickable
       - sorting, filtering - and opening an explanation is not a request to
       do that too. */
    event.stopPropagation();
    this.open = !this.open;
    if (this.open) {
      this.place();
      /* Placed twice: once from the known width so it never paints in the
         wrong corner, then again once the text has wrapped and the real
         height is known, which is what decides whether it flips above. */
      requestAnimationFrame(() => this.place());
    }
  }

  private place(): void {
    const dot = this.host.nativeElement.querySelector('.dot');
    if (!dot) return;
    const r = dot.getBoundingClientRect();
    const width = 260;
    const height = this.bubbleRef?.nativeElement.offsetHeight ?? 90;
    const gap = 8;

    /* Centred under the dot, then pulled back inside the viewport - the first
       and last columns of a wide table would otherwise hang off the edge. */
    const wanted = r.left + r.width / 2 - width / 2;
    this.left = Math.max(8, Math.min(wanted, window.innerWidth - width - 8));

    /* Below by default, above when there is no room - a hint on the last row
       of a long table has nothing but page end beneath it. */
    const below = r.bottom + gap;
    this.top = below + height > window.innerHeight - 8 ? r.top - gap - height : below;
  }

  /* Anything that moves the page moves the dot, and a fixed bubble does not
     follow it. Closing is honest; re-placing mid-scroll only looks broken. */
  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChange(): void {
    this.open = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) this.open = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.open = false;
  }
}
